import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import {
  getThirtyDaySummaryPrefill,
  updatePatientCommunication,
} from '../../services/api';
import type { PatientCommunicationResponse } from '../../services/api/types.gen';
import {
  useAutoSave,
  SaveDeclinedError,
  type AutoSaveStatus,
  type SaveOutcome,
} from '../../hooks/useAutoSave';
import { useToast } from '../../hooks/useToastContext';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import {
  getThirtyDaySummarySchema,
  getThirtyDaySummarySections,
  THIRTY_DAY_SUMMARY_PAYLOAD_KIND,
} from './thirtyDaySummaryConfig';
import { SummaryFieldRenderer } from './SummaryFieldRenderer';
import {
  applyThirtyDaySummaryPrefill,
  hasThirtyDaySummaryPrefillContent,
} from '../../utils/thirtyDaySummaryPrefill';

export interface ThirtyDaySummaryFormHandle {
  validate: () => Promise<boolean>;
  flush: () => Promise<SaveOutcome>;
}

interface ThirtyDaySummaryFormProps {
  communication: PatientCommunicationResponse;
  // The autosave hook (and its saveStatus) lives here in the form, but the
  // indicator renders in the panel's always-visible header so it stays in view
  // while this long form scrolls. The form pushes status up; the panel mirrors
  // it (a ref can't, since status changes must re-render the parent).
  onSaveStatusChange?: (status: AutoSaveStatus) => void;
}

// Hydrate the form from the persisted communication_data JSONB. Each section
// key maps to its kind-specific shape (string / string[] / nested vitals grid);
// missing keys default to an empty value so blank clinical fields stay blank.
function communicationToFormValues(
  communication: PatientCommunicationResponse
): FieldValues {
  const data = (communication.communication_data ?? {}) as Record<
    string,
    unknown
  >;
  const values: FieldValues = {};
  for (const section of getThirtyDaySummarySections()) {
    switch (section.kind) {
      case 'textarea':
      case 'single-select':
        values[section.key] = (data[section.key] as string) ?? '';
        break;
      case 'checkbox-group':
        values[section.key] = (data[section.key] as string[]) ?? [];
        if (section.allowOther) {
          values[`${section.key}_other`] =
            (data[`${section.key}_other`] as string) ?? '';
        }
        break;
      case 'vitals-grid':
        values[section.key] =
          (data[section.key] as Record<string, unknown>) ?? {};
        break;
    }
  }
  return values;
}

export const ThirtyDaySummaryForm = forwardRef<
  ThirtyDaySummaryFormHandle,
  ThirtyDaySummaryFormProps
>(({ communication, onSaveStatusChange }, ref) => {
  const { error: showError } = useToast();
  const sections = getThirtyDaySummarySections();
  const schema = getThirtyDaySummarySchema();

  const lastUpdatedRef = useRef<string | null>(
    communication.communication_data_last_updated ?? null
  );

  useEffect(() => {
    lastUpdatedRef.current =
      communication.communication_data_last_updated ?? null;
  }, [communication.id, communication.communication_data_last_updated]);

  const initialFormValues = useMemo(
    () => communicationToFormValues(communication),
    [communication.id, communication.communication_data_last_updated]
  );

  const initialSerialized = useMemo(
    () => JSON.stringify(initialFormValues),
    [initialFormValues]
  );

  const { control, register, setValue, trigger, watch, getValues } =
    useForm<FieldValues>({
      resolver: zodResolver(schema),
      defaultValues: initialFormValues,
      mode: 'onTouched',
    });

  const [prefilling, setPrefilling] = useState(false);
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);

  const fetchAndApplyPrefill = useCallback(
    async (clearFirst: boolean) => {
      setPrefilling(true);
      try {
        const res = await getThirtyDaySummaryPrefill({
          path: { communication_id: communication.id },
        });
        if (res.error || !res.data) {
          console.error('Failed to fetch 30-day summary prefill:', res.error);
          showError(
            'Prefill Failed',
            'Unable to load prefilled data from the plan of care. Please try again or fill in fields manually.'
          );
          return;
        }
        applyThirtyDaySummaryPrefill(setValue, res.data, clearFirst);
        setConfirmOverwriteOpen(false);
      } catch (err) {
        console.error('Failed to fetch 30-day summary prefill:', err);
        showError(
          'Prefill Failed',
          'Unable to load prefilled data from the plan of care. Please try again or fill in fields manually.'
        );
      } finally {
        setPrefilling(false);
      }
    },
    [communication.id, setValue, showError]
  );

  const requestPrefill = useCallback(() => {
    if (hasThirtyDaySummaryPrefillContent(getValues())) {
      setConfirmOverwriteOpen(true);
      return;
    }
    void fetchAndApplyPrefill(false);
  }, [fetchAndApplyPrefill, getValues]);

  const performSave = useCallback(
    async (jsonValues: string) => {
      const values = JSON.parse(jsonValues) as Record<string, unknown>;

      const response = await updatePatientCommunication({
        path: { communication_id: communication.id },
        body: {
          communication_data: {
            ...values,
            payload_kind: THIRTY_DAY_SUMMARY_PAYLOAD_KIND,
          },
          communication_data_last_updated: lastUpdatedRef.current,
        },
      });

      if (response.error || !response.data) {
        console.error('Failed to save 30-day summary', response.error);
        showError(
          getHttpStatus(response) === 409
            ? 'This summary was updated elsewhere. Reload it and try again.'
            : 'Failed to save. Please try again.'
        );
        throw new Error('Failed to save communication data');
      }

      // Keep the OCC token fresh so the next debounce doesn't race. The parent
      // is intentionally not refetched here to avoid
      // flashing the form back to a loading state on every keystroke.
      lastUpdatedRef.current =
        response.data.communication_data_last_updated ?? lastUpdatedRef.current;
    },
    [communication.id, showError]
  );

  const {
    setValue: setAutoSaveValue,
    saveNow,
    saveStatus,
  } = useAutoSave({
    initialValue: initialSerialized,
    onSave: useCallback(
      async (jsonValues: string) => {
        // All fields are optional today so this never blocks, but throwing
        // (not returning) keeps the form correct by construction if a required
        // field is added later: a blocked save reports "Not saved", never a
        // false "Saved".
        const ok = await trigger();
        if (!ok) throw new SaveDeclinedError();
        await performSave(jsonValues);
      },
      [trigger, performSave]
    ),
    debounceDelay: 1500,
  });

  useEffect(() => {
    const subscription = watch(formValues => {
      setAutoSaveValue(JSON.stringify(formValues));
    });
    return () => subscription.unsubscribe();
  }, [watch, setAutoSaveValue]);

  // Mirror save state up to the panel header. Every field in this form is
  // optional (see thirtyDaySummaryConfig), so trigger() always passes and a
  // "Saved" here is always truthful — no readiness guard needed (unlike the
  // comms form, which gates on required fields).
  useEffect(() => {
    onSaveStatusChange?.(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  useImperativeHandle(
    ref,
    () => ({
      validate: () => trigger(),
      flush: () => saveNow(),
    }),
    [trigger, saveNow]
  );

  return (
    <Card>
      <CardContent className='space-y-4'>
        <div className='flex flex-col gap-2 border-b pb-4'>
          <p className='text-xs text-muted-foreground'>
            Prefill from the episode&apos;s CMS-485 and SOC.
          </p>
          <div>
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={prefilling}
              onClick={() => requestPrefill()}
            >
              {prefilling ? 'Loading prefill…' : 'Prefill all data'}
            </Button>
          </div>
        </div>

        {sections.map(section => (
          <SummaryFieldRenderer
            key={section.key}
            section={section}
            control={control}
            register={register}
            setValue={setValue}
          />
        ))}

        <Dialog
          open={confirmOverwriteOpen}
          onOpenChange={setConfirmOverwriteOpen}
        >
          <DialogContent className='max-w-md'>
            <DialogHeader>
              <DialogTitle>Replace existing fields?</DialogTitle>
              <DialogDescription>
                Prefilling will overwrite matching 30-Day Summary fields.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => setConfirmOverwriteOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type='button'
                size='sm'
                onClick={() => void fetchAndApplyPrefill(true)}
                disabled={prefilling}
              >
                Replace fields
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
});

ThirtyDaySummaryForm.displayName = 'ThirtyDaySummaryForm';
